import { useDebugStore } from '@client/stores/DebugStore';
import { getFlagSync } from '@client/utils/feature-flags';
import type { AnyRoute, RegisteredRouter, Router } from '@tanstack/react-router';
import type { RoutePaths } from '@tanstack/router-core';

/**
 * Type-safe route ID (path pattern) for registered routes.
 * Use this for preloading routes by their path pattern.
 * @example '/console/$orgId/claims/details/$id/manage'
 */
export type RouteId = RoutePaths<RegisteredRouter['routeTree']>;

/**
 * Global set tracking all preloaded route IDs.
 * Prevents duplicate preloading across different preloader mechanisms
 * (idle preloader, PageController, etc.)
 */
const preloadedRoutes = new Set<string>();

/**
 * Checks if a route has already been preloaded.
 * Useful for debugging and testing.
 */
export function isRoutePreloaded(routeId: string): boolean {
    return preloadedRoutes.has(routeId);
}

/**
 * Determines whether preload logs should be displayed.
 * Logs show when BOTH:
 * - isPreloadLogsEnabled is true (checkbox in debug panel)
 * - Debug mode is active (enable-debug-info feature flag OR isForceDebugEnabledFlag)
 */
function shouldLogPreload(): boolean {
    const { isForceDebugEnabledFlag, isPreloadLogsEnabled } = useDebugStore.getState();
    const isDebugInfoEnabled = getFlagSync('enable-debug-info', false);
    // Match useIsDebugInfoEnabled logic: feature flag OR force debug
    return isPreloadLogsEnabled && (isDebugInfoEnabled || isForceDebugEnabledFlag);
}

/**
 * Pre-loads the JS chunk for a specific route
 * @param route - The route to preload
 * @param router - The TanStack router instance
 * @returns Promise that resolves when the chunk is loaded
 */
export async function preloadRouteChunk(route: AnyRoute, router: Router<any, any>): Promise<void> {
    if (!route.options?.component) {
        return;
    }

    // Skip if already preloaded
    if (preloadedRoutes.has(route.id)) {
        return;
    }

    if (shouldLogPreload()) {
        console.log('[Route Preloader] Pre-loading page', route.id);
    }
    // Let errors propagate to the global chunk error handler
    await router.loadRouteChunk(route);
    preloadedRoutes.add(route.id);
}

/**
 * Pre-loads the child JS chunks for a given route
 * @param route - The parent route whose children should be preloaded
 * @param router - The TanStack router instance
 * @returns Promise that resolves when all child chunks are loaded
 */
export async function preloadChildRouteChunks(route: AnyRoute, router: Router<any, any>): Promise<void> {
    const children = route.children as Record<string, AnyRoute> | undefined;

    if (!children || typeof children !== 'object') {
        return;
    }

    const childRoutes = Object.values(children);

    if (childRoutes.length === 0) {
        return;
    }

    const enableLogging = shouldLogPreload();

    // Let errors propagate to the global chunk error handler
    await Promise.all(
        childRoutes
            .filter((child) => child.options?.component && !preloadedRoutes.has(child.id))
            .map(async (child) => {
                if (enableLogging) {
                    console.log('[Route Preloader] Pre-loading child page', child.id);
                }
                await router.loadRouteChunk(child);
                preloadedRoutes.add(child.id);
            }),
    );
}

/**
 * Pre-loads a route's JS chunk by route ID (path pattern).
 * No params needed - just the route path like '/console/$orgId/claims/details/$id/manage'
 *
 * @param routeId - The route path pattern (e.g., '/console/$orgId/claims/details/$id/manage')
 * @param router - The TanStack router instance
 * @returns Promise that resolves when the chunk is loaded
 *
 * @example
 * // Preload a route chunk by its path pattern
 * await preloadRouteChunkById('/console/$orgId/claims/details/$id/manage', router);
 */
export async function preloadRouteChunkById(routeId: RouteId, router: Router<any, any>): Promise<void> {
    const route = router.routesById[routeId];
    if (route) {
        await preloadRouteChunk(route, router);
    } else if (shouldLogPreload()) {
        console.warn('[Route Preloader] Route not found:', routeId);
    }
}
